import * as React from 'react'
import filter from 'lodash/filter'
import {consoleFancyClient, extractData} from '@frontrow/console/src/open-api/clients'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'

// Models
import {
  type IdpOverlayStudentT,
  type IdpOverlayStudentPOST
} from '@frontrow/console/src/models/idp-overlay-student.types'
import IdpOverlayStudents from '@frontrow/console/src/components/pages/roster-page/idp-overlay-students-page/idp-overlay-students'

export default function RosterSyncPageContainer(): React.ReactElement {
  const [overlays, setOverlays] = React.useState<Array<IdpOverlayStudentT>>([])
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState<string | null>(null)

  // Never rejects: a failed fetch is shown in the error alert, including the
  // refetch that follows a successful POST.
  const fetchOverlays = async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const overlays = extractData(await consoleFancyClient.GET('/2/student-idp-overlays'))
      // The spec's `idp`/`type` fields are wider strings than the app's
      // narrower IdpT/IdpOverlayT unions.
      setOverlays(overlays as IdpOverlayStudentT[])
    } catch (err: unknown) {
      setError(firstResponseApiError(err, 'Failed to load IdP overlay students.').message)
    } finally {
      setIsLoading(false)
    }
  }

  const postOverlay = (data: IdpOverlayStudentPOST): Promise<unknown> => {
    // The spec types `value` as a string; the app's model also allows a number.
    return consoleFancyClient
      .POST('/2/student-idp-overlays', {body: {...data, value: String(data.value)}})
      .then(() => fetchOverlays())
  }

  const deleteOverlay = (id: number): Promise<unknown> => {
    return consoleFancyClient
      .DELETE('/2/student-idp-overlays/{StudentsIdpOverlayR-IdpOverlayStudentId}', {
        params: {path: {'StudentsIdpOverlayR-IdpOverlayStudentId': id}}
      })
      .then(() => setOverlays(prevOverlays => filter(prevOverlays, overlay => overlay.id !== id)))
  }

  React.useEffect(() => {
    fetchOverlays()
  }, [])

  return (
    <React.Fragment>
      {error !== null && <ErrorAlert>{error}</ErrorAlert>}
      <IdpOverlayStudents
        overlays={overlays}
        isLoading={isLoading}
        postOverlay={postOverlay}
        deleteOverlay={deleteOverlay}
      />
    </React.Fragment>
  )
}
